import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Edit2, Trash2 } from 'lucide-react';

interface SelectedOffer {
  id: string;
  title: string;
  category: string;
  quantity: number;
  price: number;
  unitType: string;
  notes?: string;
}

interface RecapCategoryStepProps {
  category: string;
  selectedOffers: SelectedOffer[];
  onComplete: () => void;
  onModify: () => void;
  onRemoveOffer: (offerId: string) => void;
}

export const RecapCategoryStep = ({ 
  category, 
  selectedOffers, 
  onComplete, 
  onModify,
  onRemoveOffer 
}: RecapCategoryStepProps) => {
  const categoryOffers = selectedOffers.filter(offer => offer.category === category);
  const categoryTotal = categoryOffers.reduce((sum, offer) => sum + (offer.price * offer.quantity), 0);

  return (
    <div className="space-y-4 p-4 max-h-[80vh] overflow-y-auto">
      <div className="mb-6">
        <h3 className="font-semibold text-lg mb-2">Récapitulatif — {category}</h3>
        <p className="text-sm text-muted-foreground">
          Voici les offres que vous avez sélectionnées pour cette catégorie.
        </p>
      </div>

      {categoryOffers.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground">
            Aucune offre sélectionnée dans cette catégorie.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {categoryOffers.map((offer) => (
            <Card key={offer.id}>
              <CardHeader className="pb-2">
                <div className="space-y-3">
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-sm font-semibold leading-tight text-foreground">{offer.title}</CardTitle>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="text-xs text-muted-foreground">Sur-mesure</div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemoveOffer(offer.id)}
                        className="text-muted-foreground hover:text-destructive h-8 w-8 p-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="secondary" className="text-xs px-2 py-1">
                      Qté: {offer.quantity}
                    </Badge>
                    {offer.unitType && offer.unitType !== 'forfait' && (
                      <Badge variant="outline" className="text-xs px-2 py-1">
                        {offer.unitType}
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              {offer.notes && (
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground italic">
                    {offer.notes}
                  </p>
                </CardContent>
              )}
            </Card>
          ))}
          
          {/* Sous-total retiré — tarifs précisés dans la proposition sur-mesure */}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between items-center pt-4 border-t gap-2">
        <Button 
          variant="outline" 
          onClick={onModify}
          className="flex items-center gap-2 text-sm px-3 py-2"
        >
          <Edit2 className="w-3 h-3" />
          Modifier
        </Button>
        
        <Button onClick={onComplete} className="text-sm px-3 py-2">
          Suivante
        </Button>
      </div>
    </div>
  );
};